import { BsGithub, BsLinkedin } from 'react-icons/bs';
import { RiContactsFill } from 'react-icons/ri';
import { MdDownload } from 'react-icons/md';

const Hero = ({ personalData }) => {
  return (
    <section
      id="home"
      className="relative flex flex-col items-center justify-center min-h-screen pt-24 pb-16 w-full"
      data-aos="fade-up"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full max-w-screen-xl mx-auto px-6">

        {/* LEFT: Hero intro */}
        <div className="flex flex-col gap-6 order-2 lg:order-1">

          {/* Eyebrow badge */}
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
              text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
              👋 Available for opportunities
            </span>
          </div>

          {/* Headline */}
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold leading-tight dark:text-white text-slate-900">
              Hi, I'm{' '}
              <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                Santi Ornelas
              </span>
            </h1>
            <p className="mt-4 text-base md:text-lg dark:text-slate-400 text-slate-600 leading-relaxed max-w-lg">
              {personalData.designation}
            </p>
          </div>

          {/* Stats row */}
          <div className="flex gap-8 py-5 border-t border-b dark:border-slate-700/50 border-slate-200">
            <div>
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">2+</p>
              <p className="text-xs dark:text-slate-400 text-slate-500 uppercase tracking-wider mt-0.5">Years Exp</p>
            </div>            
            <div>
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">2</p>
              <p className="text-xs dark:text-slate-400 text-slate-500 uppercase tracking-wider mt-0.5">Companies</p>
            </div>            
          </div>

          {/* Social links */}
          <div className="flex items-center gap-4">
            <a href={personalData.github} target="_blank" rel="noopener noreferrer"
              className="dark:text-slate-400 text-slate-500 dark:hover:text-blue-400 hover:text-blue-500 hover:scale-110 transition-all duration-200">
              <BsGithub size={24} />
            </a>
            <a href={personalData.linkedIn} target="_blank" rel="noopener noreferrer"
              className="dark:text-slate-400 text-slate-500 dark:hover:text-blue-400 hover:text-blue-500 hover:scale-110 transition-all duration-200">
              <BsLinkedin size={24} />
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-3 flex-wrap">
            <a
              href="#contact"
              className="flex items-center gap-2 px-6 py-3 rounded-full
                bg-gradient-to-r from-blue-500 to-purple-600 text-white
                text-sm font-medium hover:shadow-lg hover:shadow-blue-500/25
                hover:scale-105 transition-all duration-200"
            >
              Let's Connect <RiContactsFill size={16} />
            </a>
            <a
              href={personalData.resume}
              download="Santiago-Ornelas-Resume"
              className="flex items-center gap-2 px-6 py-3 rounded-full
                dark:border border dark:border-slate-600 border-slate-300
                dark:text-slate-300 text-slate-700
                dark:hover:border-blue-400 hover:border-blue-400
                dark:hover:text-blue-400 hover:text-blue-500
                text-sm font-medium transition-all duration-200"
            >
              Resume <MdDownload size={16} />
            </a>
          </div>
        </div>

        {/* RIGHT: Code snippet widget */}
        <div className="order-1 lg:order-2" data-aos="fade-left" data-aos-delay="200">
          <div className="dark:bg-gradient-to-r dark:from-[#0d1224] dark:to-[#0a0d37] bg-slate-50
            dark:border-[#1b2c68] border-slate-200 border rounded-2xl p-1 shadow-xl
            dark:shadow-blue-500/5 shadow-slate-200/50">
            {/* Window chrome */}
            <div className="flex items-center gap-2 px-4 py-3 dark:border-b dark:border-slate-700/50 border-b border-slate-200">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-2 text-xs dark:text-slate-500 text-slate-400 font-mono">coder.js</span>
            </div>
            <div className="px-4 lg:px-8 py-6 overflow-x-auto">
              <code className="font-mono text-xs md:text-sm lg:text-[0.85rem] leading-6 block">
                <div>
                  <span className="text-violet-400">const</span>
                  <span className="dark:text-white text-slate-800 mx-2">technologist</span>
                  <span className="text-violet-400">=</span>
                  <span className="dark:text-slate-400 text-slate-500 ml-2">{'{'}</span>
                </div>
                <div>
                  <span className="ml-4 lg:ml-8 dark:text-blue-300 text-blue-500">name</span>
                  <span className="dark:text-slate-400 text-slate-500">:</span>
                  <span className="text-amber-400 ml-2">'Santi Ornelas'</span>
                  <span className="dark:text-slate-400 text-slate-500">,</span>
                </div>
                <div className="ml-4 lg:ml-8">
                  <span className="dark:text-blue-300 text-blue-500">skills</span>
                  <span className="dark:text-slate-400 text-slate-500">: [</span>
                  <span className="text-amber-400">'React'</span>
                  <span className="dark:text-slate-400 text-slate-500">, </span>
                  <span className="text-amber-400">'Node.js'</span>
                  <span className="dark:text-slate-400 text-slate-500">, </span>
                  <span className="text-amber-400">'Python'</span>
                  <span className="dark:text-slate-400 text-slate-500">, </span>
                  <span className="text-amber-400">'PostgreSQL'</span>
                  <span className="dark:text-slate-400 text-slate-500">],</span>
                </div>
                <div>
                  <span className="ml-4 lg:ml-8 dark:text-blue-300 text-blue-500">hardWorker</span>
                  <span className="dark:text-slate-400 text-slate-500">: </span>
                  <span className="text-orange-400">true</span>
                  <span className="dark:text-slate-400 text-slate-500">,</span>
                </div>
                <div>
                  <span className="ml-4 lg:ml-8 dark:text-blue-300 text-blue-500">quickLearner</span>
                  <span className="dark:text-slate-400 text-slate-500">: </span>
                  <span className="text-orange-400">true</span>
                  <span className="dark:text-slate-400 text-slate-500">,</span>
                </div>
                <div>
                  <span className="ml-4 lg:ml-8 dark:text-blue-300 text-blue-500">problemSolver</span>
                  <span className="dark:text-slate-400 text-slate-500">: </span>
                  <span className="text-orange-400">true</span>
                  <span className="dark:text-slate-400 text-slate-500">,</span>
                </div>
                <div>
                  <span className="ml-4 lg:ml-8 text-green-400">hireable</span>
                  <span className="dark:text-slate-400 text-slate-500">: </span>
                  <span className="text-violet-400">function</span>
                  <span className="dark:text-slate-400 text-slate-500 ml-2">{'() {'}</span>
                </div>
                <div>
                  <span className="ml-8 lg:ml-16 text-violet-400">return</span>
                  <span className="dark:text-slate-400 text-slate-500 ml-2">{'('}</span>
                </div>
                <div>
                  <span className="ml-12 lg:ml-24 text-blue-400">this.</span>
                  <span className="dark:text-white text-slate-700">hardWorker</span>
                  <span className="text-amber-400 mx-1">&&</span>
                </div>
                <div>
                  <span className="ml-12 lg:ml-24 text-blue-400">this.</span>
                  <span className="dark:text-white text-slate-700">problemSolver</span>
                  <span className="text-amber-400 mx-1">&&</span>
                </div>
                <div>
                  <span className="ml-12 lg:ml-24 text-blue-400">this.</span>
                  <span className="dark:text-white text-slate-700">skills.length</span>
                  <span className="text-amber-400 mx-1">=</span>
                  <span className="text-orange-400">4</span>
                </div>
                <div><span className="ml-8 lg:ml-16 dark:text-slate-400 text-slate-500">{');'}</span></div>
                <div><span className="ml-4 lg:ml-8 dark:text-slate-400 text-slate-500">{'};'}</span></div>
                <div><span className="dark:text-slate-400 text-slate-500">{'};'}</span></div>
              </code>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Hero;
